import React, { useState, useEffect } from 'react';
import axios from 'axios';

function AdminModal({ onClose, onSelectQuote }) {
    const [users, setUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [expandedUser, setExpandedUser] = useState(null);

    const BASE_URL = process.env.NODE_ENV === 'production'
    ? (process.env.REACT_APP_IS_BETA === 'true' ? 'https://betaquotes.omntec.com' : 'https://quotes.omntec.com')
    : window.location.protocol === 'https:' 
        ? 'https://localhost:3001'
        : 'http://localhost:3001';


    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await axios.get(`${BASE_URL}/api/admin/users`);
            if (response.data.success) {
                setUsers(response.data.users);
            }
            setLoading(false);
        } catch (error) {
            console.error('Error fetching users:', error);
            setLoading(false);
        }
    };


    const toggleUserExpansion = (userId) => {
        // If there's a search term and this user has matching quotes, don't collapse
        const user = filteredUsers.find(u => u.id === userId);
        if (searchTerm && user?.shouldExpand) {
            setExpandedUser(userId); // Only allow expanding
        } else {
            // Normal toggle behavior when no search term
            setExpandedUser(expandedUser === userId ? null : userId);
        }
    };

    const filteredUsers = users.map(user => {
        
        // Search in quotes and only keep matching quotes
        const matchingQuotes = user.recent_quotes?.filter(quote => {
            const details = quote.details;
            const facilityInfo = details.facilityInfo || {};
            
            const companyMatch = (facilityInfo.company || '').toLowerCase().includes(searchTerm.toLowerCase());
            const nameMatch = (facilityInfo.name || '').toLowerCase().includes(searchTerm.toLowerCase());
            const quoteNumberMatch = quote.quote_number.toString().includes(searchTerm);
            const projectNameMatch = (facilityInfo.projectName || '').toLowerCase().includes(searchTerm.toLowerCase());
            
            return companyMatch || nameMatch || quoteNumberMatch || projectNameMatch;
        }) || [];

        // Return user with only matching quotes and a flag indicating if they should be expanded
        return {
            ...user,
            recent_quotes: searchTerm ? matchingQuotes : user.recent_quotes,
            shouldExpand: searchTerm ? matchingQuotes.length > 0 : false
        };
    }).filter(user => {
        // Keep user if their name matches or they have matching quotes
        return user.name.toLowerCase().includes(searchTerm.toLowerCase()) || user.shouldExpand;
    });

    // Update useEffect to handle auto-expansion without overriding manual expansion
    useEffect(() => {
        if (searchTerm) {
            const expandedUsers = filteredUsers
                .filter(user => user.shouldExpand)
                .map(user => user.id);
            // Only set expanded user if none is currently expanded
            if (!expandedUser && expandedUsers.length === 1) {
                setExpandedUser(expandedUsers[0]);
            }
        }
    }, [searchTerm]);

    const calculateTotalPrice = (quoteDetails) => {
        // If there are items directly in the quote details, use them
        if (quoteDetails.items && Array.isArray(quoteDetails.items)) {
            return quoteDetails.items.reduce((sum, item) => sum + item.price, 0);
        }
        
        // If no items found, return 0
        return 0;
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        
        const date = new Date(dateString);
        
        // Format to local timezone with locale-aware formatting
        return date.toLocaleString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    return (
        <div className="modal">
            <div className="modal-content admin-modal">
                <h2 style={{color: 'white'}}>User/Distributor Management</h2>
                <div className="search-container">
                    <input
                        type="text"
                        placeholder="Search users..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                </div>

                {loading ? (
                    <p>Loading...</p>
                ) : (
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>User/Distributor Name</th>
                                <th>Quote Count</th>
                                <th>Most Recent</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredUsers.map((user) => (
                                <React.Fragment key={user.id}>
                                    <tr onClick={() => toggleUserExpansion(user.id)}
                                        className={expandedUser === user.id ? 'expanded' : ''}>
                                        <td>{user.name}</td>
                                        <td>{user.quote_count}</td>
                                        <td>{formatDateTime(user.most_recent_quote_date)}</td>
                                    </tr>
                                    {(expandedUser === user.id || user.shouldExpand) && user.recent_quotes && (
                                        <tr>
                                            <td colSpan="3">
                                                <table className="recent-quotes-table">
                                                    <thead>
                                                        <tr>
                                                            <th>User - Company</th>
                                                            <th>Quote Number</th>
                                                            <th>Total List Price</th>
                                                            <th>Grand Total</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {user.recent_quotes.map((quote, index) => {
                                                            const details = quote.details;
                                                            const facilityInfo = details.facilityInfo || {};
                                                            const totalPrice = calculateTotalPrice(details);
                                                            const discount = user.discount || 2;
                                                            const grandTotal = totalPrice - (totalPrice * discount / 100);
                                                            
                                                            return (
                                                                <tr key={index} 
                                                                    onClick={() => onSelectQuote(quote)} 
                                                                    style={{ cursor: 'pointer' }}
                                                                    className="quote-row"
                                                                >
                                                                    <td>{facilityInfo.name || 'N/A'} - {facilityInfo.company || 'N/A'}</td>
                                                                    <td>{quote.quote_number}</td>
                                                                    <td>${totalPrice.toFixed(2)}</td>
                                                                    <td>${grandTotal.toFixed(2)}</td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                )}
                <button onClick={onClose} className="close-button">Close</button>
            </div>
        </div>
    );
}

export default AdminModal;
